import camelcaseKeys from 'camelcase-keys'
import { format, setYear } from 'date-fns'
import throttle from 'lodash.throttle'

import type {
  Check,
  CheckDTO,
  UpdateCheckDTO,
  UpdateChecksDTO,
} from '@/modules/projects/stores/types'
import { executeDelete, executePatch } from '@/services/lukita'
import {
  addOrUpdateItems,
  removeItems,
  retrieveItem,
  updateItem,
  updateItems,
} from '@/utils'
import { loadPages } from './paging'
import type {
  ChecksActions,
  ChecksLoaders,
  ChecksMutations,
  ProjectStoreCreator,
} from './types'

// A check is never a credit-card payment. The flag exists on Transaction to
// hide CC payments from totals; on a Check it's always true.
const normalize = (check: Check | CheckDTO): Check => ({
  ...(camelcaseKeys(check) as Check),
  isNotCCPayment: true,
})

export const checksActions: ProjectStoreCreator<ChecksActions> = (set) => ({
  startLoadingChecks: () => set({ loadingChecks: true }),
  stopLoadingChecks: () => set({ loadingChecks: false }),

  setChecks: (checks: Check[] | CheckDTO[]) =>
    set({ checks: checks.map(normalize) }),

  mergeChecks: (checks: Check[] | CheckDTO[]) =>
    set((state) => ({
      checks: addOrUpdateItems(state.checks, checks.map(normalize)),
    })),

  addChecks: (checks: Check[] | CheckDTO[]) =>
    set((state) => ({ checks: [...state.checks, ...checks.map(normalize)] })),

  setCheck: (check: Check | CheckDTO) =>
    set((state) => ({ checks: updateItem(state.checks, normalize(check)) })),

  updateChecks: (checks: Check[] | CheckDTO[]) =>
    set((state) => ({
      checks: updateItems(state.checks, checks.map(normalize)),
    })),

  deleteChecks: (ids: number[]) =>
    set((state) => ({ checks: removeItems(state.checks, ids) })),
})

export const checksMutations: ProjectStoreCreator<ChecksMutations> = (
  set,
  get,
) => ({
  updateCheck: async (id, body) => {
    set({ mutating: true })
    const { project, checks } = get()
    const check = retrieveItem(checks, id)
    if (!check || !project) {
      set({ mutating: false })
      return
    }

    const path = `/projects/${project.slug}/checks/${id}`
    const response = await executePatch(path, body).finally(() =>
      set({ mutating: false }),
    )

    const updatedCheck = await response.json()
    get().actions.setCheck(updatedCheck)
    return updatedCheck
  },

  updateChecks: async (checks, body) => {
    set({ mutating: true })
    const project = get().project
    const ids = checks.map(({ id }) => id)
    if (!ids.length || !project) {
      set({ mutating: false })
      return
    }

    const path = `/projects/${project.slug}/checks/`
    await executePatch(path, { ids, data: body }).finally(() =>
      set({ mutating: false }),
    )

    // Apply the same edit locally so the table reflects the change without a
    // refetch. Mirrors the transactions bulk update; year rolls each row's
    // existing month/day to the new year, other fields are simple overwrites.
    const idSet = new Set(ids)
    const parsedBody = camelcaseKeys(body)

    const applyEdit = (check: Check): Check => {
      const { year, ...updates } = parsedBody
      const updatedDate =
        year && format(setYear(check.date, Number(year)), 'yyyy-MM-dd')

      return {
        ...check,
        ...updates,
        ...(year && { date: updatedDate as string }),
      }
    }

    const updatedChecks = get()
      .checks.filter(({ id }) => idSet.has(id))
      .map(applyEdit)

    get().actions.updateChecks(updatedChecks)
  },

  deleteChecks: async (checks) => {
    const project = get().project
    const ids = checks.map(({ id }) => id)
    get().actions.deleteChecks(ids)

    try {
      const path = `/projects/${project?.slug}/checks`
      await executeDelete(path, { ids })
    } catch (error) {
      get().actions.addChecks(checks)
      throw error
    }
  },
})

export const checksLoaders: ProjectStoreCreator<ChecksLoaders> = (
  set,
  get,
) => ({
  fetchChecks: throttle(async (slug: string) => {
    if (get().project?.slug !== slug) get().actions.startLoadingChecks()

    const hasLoadedList = !get().loadingChecks && !get().loadingMoreChecks

    const loadId = get().checksLoadId + 1
    const isStale = () => get().checksLoadId !== loadId
    set({ checksLoadId: loadId })

    const { setChecks, mergeChecks } = get().actions
    try {
      await loadPages<CheckDTO>({
        path: `/projects/${slug}/checks`,
        root: 'checks',
        mode: hasLoadedList ? 'whole' : 'progressive',
        isStale,
        store: (rows, { isFirst }) =>
          isFirst ? setChecks(rows) : mergeChecks(rows),
        onProgress: ({ hasMore }) =>
          set({ loadingChecks: false, loadingMoreChecks: hasMore }),
      })
    } finally {
      if (!isStale()) set({ loadingMoreChecks: false })
    }
  }, 500),
})

export type { UpdateCheckDTO, UpdateChecksDTO }
